import posthog from 'posthog-js';
import { UserAnswers } from '@/types/dogs';

// Types for analytics events
type QuizEvent = {
  category: 'quiz';
  action: 'start' | 'complete' | 'abandon' | 'progress' | 'click_characteristic' | 'click_care_tip';
  questionNumber?: number;
  answers?: Record<string, string>;
  question?: string;
  answer?: string;
  breed?: string;
  characteristic?: string;
  tip?: string;
};

type BreedEvent = {
  category: 'breed';
  action: 'view' | 'click_care_tip' | 'click_characteristic';
  breedName: string;
  characteristic?: string;
  tip?: string;
};

type NavigationEvent = {
  category: 'navigation';
  action: 'click';
  destination: string;
};

type PerformanceEvent = {
  category: 'performance';
  action: 'page_load' | 'image_load';
  duration: number;
  page: string;
};

type ErrorEvent = {
  category: 'error';
  action: 'client_error' | 'api_error';
  message: string;
  stack?: string;
};

type AnalyticsEvent = 
  | QuizEvent 
  | BreedEvent 
  | NavigationEvent 
  | PerformanceEvent 
  | ErrorEvent;

type DataLayerEvent = {
  event: string;
  category: string;
  action: string;
  [key: string]: unknown;
};

// Helper to ensure window.dataLayer exists
const ensureDataLayer = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
};

// Main analytics tracking function
export const trackEvent = (event: AnalyticsEvent) => {
  ensureDataLayer();
  if (typeof window !== 'undefined') {
    window.dataLayer.push({
      event: `${event.category}_${event.action}`,
      ...event
    });
  }
};

// Quiz funnel steps
export const QUIZ_FUNNEL_STEPS = {
  START: 'quiz_start',
  QUESTION_1: 'quiz_question_1',
  QUESTION_2: 'quiz_question_2',
  QUESTION_3: 'quiz_question_3',
  QUESTION_4: 'quiz_question_4',
  QUESTION_5: 'quiz_question_5',
  COMPLETE: 'quiz_complete',
  ABANDON: 'quiz_abandon'
} as const;

// Specific tracking functions for different event types
export function trackQuizEvent(event: string, properties?: Record<string, unknown>) {
  posthog.capture(`quiz_${event}`, {
    ...properties,
    timestamp: new Date().toISOString()
  });
}

export function trackBreedEvent(event: string, breed: string, properties?: Record<string, unknown>) {
  posthog.capture(`breed_${event}`, {
    breed,
    ...properties,
    timestamp: new Date().toISOString()
  });
}

export function trackCharacteristicClick(characteristic: string) {
  posthog.capture('characteristic_clicked', {
    characteristic,
  });
}

export function trackCareTipClick(tip: string) {
  posthog.capture('care_tip_clicked', {
    tip,
  });
}

// Quiz funnel tracking functions
export const trackQuizStart = () => {
  posthog.capture('quiz_started');
};

export const trackQuizQuestion = (questionNumber: number, question: string, answer: string) => {
  posthog.capture('quiz_question_answered', {
    question_number: questionNumber,
    question,
    answer,
  });
};

export const trackQuizComplete = (breed: string, answers: Partial<UserAnswers>) => {
  posthog.capture('quiz_completed', {
    breed,
    answers: Object.entries(answers).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: String(value),
    }), {}),
  });
};

export const trackQuizAbandon = (questionNumber: number, answers: Partial<UserAnswers>) => {
  posthog.capture('quiz_abandoned', {
    question_number: questionNumber,
    answers: Object.entries(answers).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: String(value),
    }), {}),
  });
};

// Navigation tracking
export function trackNavigation(destination: string) {
  posthog.capture('navigation', {
    destination,
    timestamp: new Date().toISOString()
  });
}

// Performance tracking
export function trackPerformance(action: string, duration: number, page: string) {
  posthog.capture('performance', {
    action,
    duration,
    page,
    timestamp: new Date().toISOString()
  });
}

// Error tracking
export function trackError(action: string, message: string, stack?: string) {
  posthog.capture('error', {
    action,
    message,
    stack,
    timestamp: new Date().toISOString()
  });
}

// Type declaration for window.dataLayer
declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
  }
} 